import type { WorkspaceConfig } from "@/shared";

import { core } from "@tauri-apps/api";
import { listen, type UnlistenFn } from "@tauri-apps/api/event";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { debugProductivity } from "@/domains/logging/utils/debugLog";
import { getSafeAspectColor } from "@/shared";

import { isBuzzerInterceptionEnabled } from "./buzzerInterception";

interface UseBuzzerControlProperties {
  config: Record<string, Record<string, unknown>>;
  isCompletionDialogOpen?: boolean;
  isTracking: boolean;
  onProximityDetected?: () => void;
  onProximityLost?: () => void;
  startTracking: (workspace: string, aspect: string, color?: string) => void;
  stopTracking: (notes?: string) => void;
}

/**
 * WHAT:
 *   Hook that manages buzzer control for time tracking with workspace/aspect
 *   selection and proximity detection.
 *
 * WHY:
 *   Enables hardware buzzer integration for intuitive time tracking with
 *   rotation-based navigation and automatic tracking start/stop.
 *
 * BEHAVIOR:
 *   - Manages workspace/aspect selection via rotation
 *   - Handles buzzer press events for tracking start/stop
 *   - Detects proximity for automatic actions
 *   - Provides current selection state
 *   - Updates mode (workspace/aspect) based on selection
 *
 * RISKS:
 *   - NOTE: Buzzer events may be missed if handler is too slow
 *   - NOTE: Proximity detection may trigger false positives
 *   - NOTE: State synchronization between refs and state may cause issues
 *
 * @param root0
 *
 * @param root0.config
 *
 * @param root0.isCompletionDialogOpen
 *
 * @param root0.isTracking
 *
 * @param root0.onProximityDetected
 *
 * @param root0.onProximityLost
 *
 * @param root0.startTracking
 *
 * @param root0.stopTracking
 *
 * @returns Buzzer control state and handlers
 *
 */
export function useBuzzerControl({
  config,
  isCompletionDialogOpen,
  isTracking,
  onProximityDetected,
  onProximityLost,
  startTracking,
  stopTracking,
}: UseBuzzerControlProperties) {
  const [mode, setMode] = useState<"aspect" | "workspace">("workspace");
  const [confirmedWorkspace, setConfirmedWorkspace] = useState<null | string>(null);
  const [currentSelectionIndex, setCurrentSelectionIndex] = useState(0);
  const [proximityState, setProximityState] = useState(false);
  const [lastHeartbeat, setLastHeartbeat] = useState<number>(0);

  // Debouncing für Rotation-Events
  const lastRotationTime = useRef<number>(0);
  const ROTATION_DEBOUNCE_MS = 100; // 100ms Debounce

  // Debouncing für Proximity-Events
  const lastProximityTime = useRef<number>(0);
  const PROXIMITY_DEBOUNCE_MS = 2500; // 2.5 Sekunden Debounce

  type ProductivityLogLevel = "debug" | "error" | "info" | "warn";

  type DebugProductivityFunction = (
    level: ProductivityLogLevel,
    domain: string,
    message: string,
    context?: Record<string, unknown>,
  ) => void;

  const safeDebugProductivity = debugProductivity as DebugProductivityFunction;

  // Auto-start buzzer on mount
  useEffect(() => {
    const startBuzzer = async () => {
      try {
        await core.invoke("start_buzzer");
      } catch {
        // ignore
      }
    };
    void startBuzzer();
  }, []);

  // Filter out administrative workspaces that shouldn't be used for time tracking
  const excludedWorkspaces = new Set(["settings"]);

  // Calculate workspace options directly
  const allKeys = Object.keys(config);

  const workspaceOptions = allKeys.filter((workspace) => {
    const isExcluded = excludedWorkspaces.has(workspace);
    return !isExcluded;
  });

  // Calculate aspect options directly
  const aspectOptions = useMemo(() => {
    return confirmedWorkspace ? Object.keys(config[confirmedWorkspace] ?? {}) : [];
  }, [confirmedWorkspace, config]);

  const currentOptions = useMemo(() => {
    return mode === "workspace" ? workspaceOptions : aspectOptions;
  }, [mode, workspaceOptions, aspectOptions]);

  // Reset selection index when options change
  useEffect(() => {
    if (currentOptions.length > 0 && currentSelectionIndex >= currentOptions.length) {
      setCurrentSelectionIndex(0);
    }
  }, [currentOptions, currentSelectionIndex]);

  // Debug log when selection changes
  useEffect(() => {
    // selection changed - no side effects required
  }, [currentSelectionIndex, currentOptions, mode]);

  // Refs to store current state for event handler
  const stateReference = useRef({
    config: {} as Record<string, Record<string, unknown>>,
    confirmedWorkspace: null as null | string,
    currentOptions: [] as string[],
    currentSelectionIndex: 0,
    isCompletionDialogOpen: isCompletionDialogOpen ?? false,
    isTracking,
    mode,
    onProximityDetected,
    onProximityLost,
    startTracking,
    stopTracking,
  });

  // Update ref when state changes
  useEffect(() => {
    stateReference.current = {
      config,
      confirmedWorkspace,
      currentOptions,
      currentSelectionIndex,
      isCompletionDialogOpen: isCompletionDialogOpen ?? false,
      isTracking,
      mode,
      onProximityDetected,
      onProximityLost,
      startTracking,
      stopTracking,
    };
  }, [
    isTracking,
    mode,
    currentOptions,
    currentSelectionIndex,
    confirmedWorkspace,
    config,
    startTracking,
    stopTracking,
    isCompletionDialogOpen,
    onProximityDetected,
    onProximityLost,
  ]);

  /**
   * WHAT:
   *   Handles workspace selection when in workspace mode.
   *
   * @param selected - Selected workspace name
   *
   */
  const handleWorkspaceSelection = useCallback((selected: string) => {
    safeDebugProductivity("info", "timetracq", "Workspace selected, switching to aspect mode", {
      selected_workspace: selected,
      selection_index: stateReference.current.currentSelectionIndex,
    });
    setConfirmedWorkspace(selected);
    setMode("aspect");
    setCurrentSelectionIndex(0);
  }, []);

  /**
   * WHAT:
   *   Handles aspect selection and starts tracking.
   *
   * @param workspace - Selected workspace name
   *
   * @param aspect - Selected aspect name
   *
   */
  const handleAspectSelection = useCallback((workspace: string, aspect: string) => {
    const state = stateReference.current;
    safeDebugProductivity("info", "timetracq", "Aspect selected, starting tracking", {
      aspect,
      selection_index: state.currentSelectionIndex,
      workspace,
    });
    const color = getSafeAspectColor(state.config as WorkspaceConfig, workspace, aspect);
    safeDebugProductivity("info", "timetracq", "Calling startTracking", {
      aspect,
      color,
      workspace,
    });
    state.startTracking(workspace, aspect, color);
    setConfirmedWorkspace(null);
    setMode("workspace");
    setCurrentSelectionIndex(0);
  }, []);

  /**
   * WHAT:
   *   Handles tracking stop when buzzer is pressed during tracking.
   */
  const handleTrackingStop = useCallback(() => {
    const state = stateReference.current;
    if (state.isCompletionDialogOpen) {
      safeDebugProductivity(
        "warn",
        "timetracq",
        "Tracking active but completion dialog already open, ignoring press",
        {
          is_tracking: state.isTracking,
        },
      );
      return;
    }
    safeDebugProductivity("info", "timetracq", "Tracking active, calling stopTracking", {
      is_tracking: state.isTracking,
    });
    state.stopTracking();
  }, []);

  /**
   * WHAT:
   *   Handles buzzer press event based on current state.
   */
  const handleBuzzerPress = useCallback(() => {
    const state = stateReference.current;

    safeDebugProductivity("info", "timetracq", "Buzzer press event received", {
      confirmed_workspace: state.confirmedWorkspace,
      current_options_count: state.currentOptions.length,
      current_selection_index: state.currentSelectionIndex,
      is_tracking: state.isTracking,
      mode: state.mode,
    });

    if (!state.isTracking && state.mode === "workspace") {
      const selected = state.currentOptions[state.currentSelectionIndex];
      if (selected) {
        handleWorkspaceSelection(selected);
      }
    } else if (!state.isTracking && state.mode === "aspect" && state.confirmedWorkspace) {
      const workspace = state.confirmedWorkspace.trim();
      const aspect = state.currentOptions[state.currentSelectionIndex]?.trim();
      if (aspect) {
        handleAspectSelection(workspace, aspect);
      }
    } else if (state.isTracking) {
      handleTrackingStop();
    } else {
      safeDebugProductivity("warn", "timetracq", "Press event in unexpected state", {
        confirmed_workspace: state.confirmedWorkspace,
        is_tracking: state.isTracking,
        mode: state.mode,
      });
    }
  }, [handleWorkspaceSelection, handleAspectSelection, handleTrackingStop]);

  const handleRotation = useCallback((direction: "left" | "right") => {
    // Debouncing: Ignoriere Events die zu schnell aufeinander folgen
    const now = Date.now();
    if (now - lastRotationTime.current < ROTATION_DEBOUNCE_MS) {
      return;
    }
    lastRotationTime.current = now;

    // Verwende stateRef für aktuelle Werte
    const currentState = stateReference.current;

    // Verwende direkt stateRef.current.currentOptions
    const options = currentState.currentOptions;

    if (options.length === 0) {
      return;
    }

    setCurrentSelectionIndex((previousIndex) => {
      const length = options.length;
      let next = direction === "right" ? previousIndex + 1 : previousIndex - 1;
      if (next < 0) {
        next = length - 1;
      }
      if (next >= length) {
        next = 0;
      }

      return next;
    });
  }, []); // Keine Dependencies mehr nötig, da wir stateRef verwenden

  // Event listener setup - Setup immediately on mount
  useEffect(() => {
    let unlistenBuzzer: null | UnlistenFn = null;

    const setupListener = async () => {
      try {
        // Main buzzer event listener - setup once
        unlistenBuzzer = await listen("buzzer-event", (e) => {
          const event = e.payload as { event_type: string; value?: number };

          switch (event.event_type) {
            case "rotation_right": {
              handleRotation("right");
              break;
            }

            case "rotation_left": {
              handleRotation("left");
              break;
            }

            case "press": {
              if (isBuzzerInterceptionEnabled()) {
                // In Test/Interception-Mode: keine Tracking-Logik ausführen
                break;
              }
              handleBuzzerPress();
              break;
            }
            case "proximity_on": {
              setProximityState(true);

              // Proximity Callback mit Debouncing
              const state = stateReference.current;
              if (state.isTracking && state.onProximityDetected) {
                const now = Date.now();
                const timeSinceLast = now - lastProximityTime.current;
                // Debouncing: Verhindere mehrfaches Öffnen bei wiederholten Events
                if (timeSinceLast >= PROXIMITY_DEBOUNCE_MS) {
                  lastProximityTime.current = now;
                  safeDebugProductivity(
                    "info",
                    "timetracq",
                    "Proximity detected during tracking, calling onProximityDetected",
                    {
                      is_tracking: state.isTracking,
                      time_since_last: timeSinceLast,
                    },
                  );
                  state.onProximityDetected();
                } else {
                  safeDebugProductivity("debug", "timetracq", "Proximity detected but debounced", {
                    debounce_ms: PROXIMITY_DEBOUNCE_MS,
                    time_since_last: timeSinceLast,
                  });
                }
              }
              break;
            }

            case "proximity_off": {
              setProximityState(false);

              // Proximity Lost Callback
              const state = stateReference.current;
              if (state.onProximityLost) {
                safeDebugProductivity(
                  "info",
                  "timetracq",
                  "Proximity lost, calling onProximityLost",
                  {
                    is_tracking: state.isTracking,
                  },
                );
                state.onProximityLost();
              }
              break;
            }

            case "heartbeat": {
              const ts = Date.now();
              setLastHeartbeat(ts);
              break;
            }

            default:
          }
        });
      } catch {
        // ignore
      }
    };

    // Setup immediately on mount
    void setupListener();

    return () => {
      unlistenBuzzer?.();
    };
  }, [handleRotation]); // Setup once; handleRotation is stable via useCallback

  // NOTE: Config/state change effects removed - were empty and not needed

  return {
    confirmedWorkspace,
    currentOptions,
    currentSelectionIndex,
    lastHeartbeat,
    mode,
    proximityState,
  };
}
